import { List, Map as ImmutableMap } from 'immutable';
import { getDataType, getDimensions, getMetrics, getSort } from 'reporting-data/report/configReportGetters';
import { isUnifiedEventsQLReport } from 'reporting-data/tsTypes/reportTypes';
import { getMetricSortOptions, getPreexistingSortOption, isSortableDimension } from '../utils';
import useFetchPropertyMetaData from 'reporting-data/hooks/useFetchPropertyMetaData';
import { useMemo } from 'react';
import { isSucceeded } from 'reporting-data/asyncData/AsyncData';
const useFetchSortablePropertyOptionsForDropdown = report => {
  const dataType = getDataType(report);
  const metrics = getMetrics(report);
  const dimensions = useMemo(() => {
    // sorting by dimensions is not supported for UEQL reports
    return isUnifiedEventsQLReport(report) ? List() : getDimensions(report);
  }, [report]);
  const metricAndDimensionProperties = useMemo(() => {
    const metricProperties = metrics.map(metric => metric.get('property')).toList().toJS();
    const dimensionProperties = dimensions.toList().toJS();
    return [...metricProperties, ...dimensionProperties];
  }, [metrics, dimensions]);

  // If the preexisting sort is not a metric or dimension, we want to generate
  // an option for it as well, but if it is a metric or dimension, it is already
  // accounted for, so this can be undefined.
  const untrackedPrexistingSort = useMemo(() => {
    var _getSort;
    const sort = (_getSort = getSort(report)) === null || _getSort === void 0 ? void 0 : _getSort.first();
    const sortProperty = sort === null || sort === void 0 ? void 0 : sort.get('property');
    return metricAndDimensionProperties.includes(sortProperty) ? undefined : sort;
  }, [report, metricAndDimensionProperties]);
  const propertyArgForMetaDataFetch = useMemo(() => {
    const maybeSortProperty = untrackedPrexistingSort ? [untrackedPrexistingSort.get('property')] : [];
    return [...metricAndDimensionProperties, ...maybeSortProperty].map(property => ({
      dataType,
      property
    }));
  }, [dataType, metricAndDimensionProperties, untrackedPrexistingSort]);
  const propertyMetaDataFetch = useFetchPropertyMetaData(propertyArgForMetaDataFetch);
  const dropdownOptions = useMemo(() => {
    if (!isSucceeded(propertyMetaDataFetch)) {
      return [];
    }
    const propertyMetaData = propertyMetaDataFetch.data[dataType];
    const metricOptions = metrics.flatMap(metric => getMetricSortOptions(propertyMetaData[metric.get('property')], metric)).toJS();
    const dimensionOptions = dimensions.map(dimension => propertyMetaData[dimension] || ImmutableMap()).filter(property => isSortableDimension(property, dataType)).map(property => ({
      value: property.name,
      text: property.label
    })).toList().toJS();
    const metricAndDimensionOptions = [...metricOptions, ...dimensionOptions];
    const sortOption = getPreexistingSortOption(untrackedPrexistingSort, propertyMetaData);
    return sortOption ? metricAndDimensionOptions.concat(sortOption) : metricAndDimensionOptions;
  }, [dimensions, propertyMetaDataFetch, metrics, dataType, untrackedPrexistingSort]);
  return dropdownOptions;
};
export default useFetchSortablePropertyOptionsForDropdown;